import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import Pagination from '../components/Pagination';
import { usePageSize } from '../hooks/usePagination';
import './PushNotifications.css';
import { AuthContext } from '../context/AuthContext';

const PushNotifications = () => {
  const { hasPermission } = useContext(AuthContext);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = usePageSize();
  const [filterStatus, setFilterStatus] = useState('');
  const [sending, setSending] = useState({});

  useEffect(() => {
    fetchNotifications();
  }, [page, pageSize, filterStatus]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pageSize,
        ...(filterStatus && { status: filterStatus })
      };
      const response = await axios.get(`${API_BASE_URL}/admin/push-notifications`, { params });
      setNotifications(response.data.data || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch notifications');
      setLoading(false);
    }
  };


  const handleSend = async (id) => {
    if (!window.confirm('Are you sure you want to send this notification to all target users?')) {
      return;
    }

    setSending(prev => ({ ...prev, [id]: true }));
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/push-notifications/${id}/send`);
      setSuccess(`Notification sent! Sent: ${response.data.data.sentCount}, Failed: ${response.data.data.failedCount}`);
      setTimeout(() => setSuccess(''), 5000);
      fetchNotifications();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send notification');
    } finally {
      setSending(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/admin/push-notifications/${id}`);
      fetchNotifications();
    } catch (error) {
      setError('Failed to delete notification');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { class: 'badge-secondary', label: 'Draft' },
      scheduled: { class: 'badge-primary', label: 'Scheduled' },
      sent: { class: 'badge-success', label: 'Sent' },
      failed: { class: 'badge-danger', label: 'Failed' }
    };
    const badge = badges[status] || badges.draft;
    return <span className={`badge ${badge.class}`}>{badge.label}</span>;
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleString();
  };

  if (loading && notifications.length === 0) {
    return (
      <Layout>
        <div className="container">
          <div className="notifications-header">
            <h1>Push Notifications</h1>
            <div className="skeleton skeleton-button" style={{ width: '180px', height: '40px', borderRadius: '8px' }}></div>
          </div>
          <div className="notifications-filters">
            <div className="skeleton skeleton-text" style={{ width: '200px', height: '44px', borderRadius: '10px' }}></div>
          </div>
          <div className="notifications-table-container">
            <table className="notifications-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Message</th>
                  <th>Target</th>
                  <th>Status</th>
                  <th>Scheduled</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-image" style={{ width: '60px', height: '60px', borderRadius: '8px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80%', height: '16px', marginBottom: '4px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '90%', height: '14px', marginBottom: '4px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '70%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '60px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '24px', borderRadius: '12px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '120px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '120px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="notifications-header">
          <h1>Push Notifications</h1>
          {hasPermission('push-notifications.create') && (
            <Link to="/sy-admin/push-notifications/new" className="btn btn-primary">
              Create Notification
            </Link>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="notifications-filters">
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} className="filter-select">
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        {notifications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔔</div>
            <h2>No notifications found</h2>
            <p>No push notifications match your search criteria.</p>
            {hasPermission('push-notifications.create') && (
              <Link to="/sy-admin/push-notifications/new" className="btn btn-primary" style={{ marginTop: '16px' }}>
                Create Your First Notification
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="notifications-table-container">
              <table className="notifications-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Title</th>
                    <th>Message</th>
                    <th>Target</th>
                    <th>Status</th>
                    <th>Scheduled</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((notification) => {
                    const isSending = sending[notification._id];
                    return (
                      <tr key={notification._id}>
                        <td>
                          <div className="notification-image-cell">
                            {notification.image ? (
                              <img
                                src={`${API_BASE_URL_WITHOUT_API}${notification.image}`}
                                alt={notification.title}
                                className="notification-table-image"
                              />
                            ) : (
                              <div className="notification-image-placeholder">
                                <span className="placeholder-icon">🔔</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="notification-title-cell">
                            <span className="notification-title-text">{notification.title}</span>
                          </div>
                        </td>
                        <td>
                          <div className="notification-message-cell">
                            <span className="notification-message-text" title={notification.message}>
                              {notification.message.length > 60 
                                ? notification.message.substring(0, 60) + '...' 
                                : notification.message}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="notification-target-cell">
                            <span className="target-badge">
                              {notification.target === 'all' 
                                ? 'All Users' 
                                : notification.target === 'specific' 
                                ? `${notification.targetUsers?.length || 0} Users` 
                                : `Topic: ${notification.topic}`}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="notification-status-cell">
                            {getStatusBadge(notification.status)}
                          </div>
                        </td>
                        <td>
                          <div className="notification-scheduled-cell">
                            <span>{notification.scheduledAt ? formatDate(notification.scheduledAt) : '—'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="notification-created-cell">
                            <span>{formatDate(notification.createdAt)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="table-actions">
                            {notification.status === 'draft' && (
                              <button
                                onClick={() => handleSend(notification._id)}
                                className="btn btn-send btn-icon-only"
                                disabled={isSending}
                                title="Send Notification"
                              >
                                <span className="btn-icon">{isSending ? '⏳' : '📤'}</span>
                              </button>
                            )}
                            {hasPermission('push-notifications.update') && (
                              <button
                                onClick={() => navigate(`/sy-admin/push-notifications/edit/${notification._id}`)}
                                className="btn btn-edit btn-icon-only"
                                title="Edit Notification"
                              >
                                <span className="btn-icon">✏️</span>
                              </button>
                            )}
                            {hasPermission('push-notifications.delete') && (
                              <button
                                onClick={() => handleDelete(notification._id)}
                                className="btn btn-delete btn-icon-only"
                                title="Delete Notification"
                              >
                                <span className="btn-icon">🗑️</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              pages={totalPages}
              total={total}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              loading={loading}
              noun="notifications"
            />
          </>
        )}
      </div>
    </Layout>
  );
};

export default PushNotifications;

